import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import styled from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout/flex/Flex'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { VisibleScrollbarStyle } from '@/styles/scrollbar'
import { TransactionStatus } from '../types/payment.types'

import type { ReactNode } from 'react'

export type StatusConfig = {
  title?: string
  body?: string | ReactNode
}

type PaymentStatusProps = {
  status: TransactionStatus
  successConfig?: StatusConfig
  failedConfig?: StatusConfig
  loadingText?: string
  className?: string
}

export const PaymentStatus = ({
  status,
  successConfig,
  failedConfig,
  loadingText = t({
    id: 'paymentStatus.loading',
    message: 'Even geduld, we verwerken je betaling',
  }),
  className,
}: PaymentStatusProps) => {
  return (
    <StContainer className={className}>
      <StContent>
        {status === TransactionStatus.PROCESSING ? (
          <StLoadingContainer>
            <Loader small />
            <StBody textAlign='center'>{loadingText}</StBody>
          </StLoadingContainer>
        ) : status === TransactionStatus.REFUSED ? (
          <StPaymentStatusContainer>
            <StStatusImage src='/img/payment-failed.jpg' alt='Payment failed' />
            <StStatusTextContainer>
              <>
                <StTitle>
                  {failedConfig?.title ??
                    t({
                      id: 'paymentStatus.failed.title',
                      message: 'Betaling mislukt',
                    })}
                </StTitle>
                <StBody>
                  {failedConfig?.body ??
                    t({
                      id: 'paymentStatus.failed.body',
                      message:
                        'Er ging iets mis bij het verwerken van je betaling. Controleer je betaalgegevens of probeer het opnieuw.',
                    })}
                </StBody>
              </>
            </StStatusTextContainer>
          </StPaymentStatusContainer>
        ) : status === TransactionStatus.COMPLETED ? (
          <StPaymentStatusContainer>
            <StStatusImage src='/img/payment-success.jpg' alt='Payment succeeded' />
            <StStatusTextContainer>
              <>
                <StTitle>
                  {successConfig?.title ??
                    t({
                      id: 'paymentStatus.success.title',
                      message: 'Betaling geslaagd',
                    })}
                </StTitle>
                <StBody>
                  {successConfig?.body ??
                    t({
                      id: 'paymentStatus.success.body',
                      message: 'Bedankt! Je betaling is succesvol verwerkt. Je ontvangt binnenkort een email.',
                    })}
                </StBody>

                {/* For now, we always show the success card */}
                {successConfig?.body ? null : (
                  <StSuccessCard>
                    <FontAwesomeIcon size='lg' icon={['fal', 'message']} />
                    <div>
                      {t({
                        id: 'paymentStatus.success.card',
                        message: 'We nemen binnen 3 werkdagen contact met je op om een afspraak in te plannen.',
                      })}
                    </div>
                  </StSuccessCard>
                )}
              </>
            </StStatusTextContainer>
          </StPaymentStatusContainer>
        ) : null}
      </StContent>
    </StContainer>
  )
}

const StContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const StContent = styled.div`
  ${VisibleScrollbarStyle}
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  ${({ theme }) => `
        gap: ${theme.UI.SpacingPx.Space6};
      `}
`

const StLoadingContainer = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
  height: 100%;

  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StPaymentStatusContainer = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
  height: 100%;

  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StStatusImage = styled.img`
  max-width: 220px;
  width: 50%;
  object-fit: cover;
`

const StStatusTextContainer = styled(FlexColumn)`
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StTitle = styled(H3)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  text-align: left;
  margin: 0;
`

const StBody = styled.span<{ textAlign?: 'left' | 'center' | 'right' }>`
  font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
  line-height: ${({ theme }) => theme.Typography.BodyMediumRegular['line-height']};
  font-weight: ${({ theme }) => theme.Typography.BodyMediumRegular['font-weight']};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};

  text-align: ${({ textAlign }) => textAlign};
`

const StSuccessCard = styled(Card)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};

  font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
  font-weight: 500;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};

  border: 1px solid ${({ theme }) => theme.Components.Card.Border};
  border-radius: 12px;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4}
    ${({ theme }) => theme.UI.SpacingPx.Space6};
`
